import React, { createContext, useContext, useReducer, ReactNode } from 'react';

interface AppState {
  projects: any[];
  loading: boolean;
  error: string | null;
  selectedLanguage: 'en' | 'zh';
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PROJECTS'; payload: any[] }
  | { type: 'ADD_PROJECT'; payload: any }
  | { type: 'UPDATE_PROJECT'; payload: { id: string; updates: any } }
  | { type: 'DELETE_PROJECT'; payload: string }
  | { type: 'SET_LANGUAGE'; payload: 'en' | 'zh' };

const initialState: AppState = {
  projects: [],
  loading: false,
  error: null,
  selectedLanguage: 'en',
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload, loading: false, error: null };
    case 'ADD_PROJECT':
      return { ...state, projects: [...state.projects, action.payload] };
    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map((project) =>
          project.id === action.payload.id
            ? { ...project, ...action.payload.updates }
            : project
        ),
      };
    case 'DELETE_PROJECT':
      return {
        ...state,
        projects: state.projects.filter((project) => project.id !== action.payload),
      };
    case 'SET_LANGUAGE':
      return { ...state, selectedLanguage: action.payload };
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Helper functions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setProjects: (projects: any[]) => void;
  addProject: (project: any) => void;
  updateProject: (id: string, updates: any) => void;
  deleteProject: (id: string) => void;
  setLanguage: (language: 'en' | 'zh') => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Helper functions to simplify common operations
  const setLoading = (loading: boolean) => dispatch({ type: 'SET_LOADING', payload: loading });
  const setError = (error: string | null) => dispatch({ type: 'SET_ERROR', payload: error });
  const setProjects = (projects: Project[]) => dispatch({ type: 'SET_PROJECTS', payload: projects });
  const addProject = (project: Project) => dispatch({ type: 'ADD_PROJECT', payload: project });
  const updateProject = (id: string, updates: Partial<Project>) =>
    dispatch({ type: 'UPDATE_PROJECT', payload: { id, updates } });
  const deleteProject = (id: string) => dispatch({ type: 'DELETE_PROJECT', payload: id });
  const setLanguage = (language: 'en' | 'zh') => dispatch({ type: 'SET_LANGUAGE', payload: language });

  const value: AppContextType = {
    state,
    dispatch,
    setLoading,
    setError,
    setProjects,
    addProject,
    updateProject,
    deleteProject,
    setLanguage,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};